import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import {
  createAnswerItemsFromValues,
  getAnswerValuesFromItems
} from 'components/Assessments/helpers'
import { ServicePlanFormData } from 'components/Assessments/ServicePlanAssessment/types/ServicePlanFormData'
import { ServicePlanQuestionnaireItem } from './questionnaire'
import { ServicePlanFormField } from './formField'
import { ServicePlanTaskInfo } from './formData'
import { isFilledServicePlanTask } from './utils/helpers'

/**
 * Service Plan subsection adapter for Service Plan Assessment
 * Handles service plan tasks extraction and transformation
 */
export class ServicePlanAdapter extends BaseSubsectionAdapter<ServicePlanFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<ServicePlanFormData> {
    const taskInfoQuestionnaireAnswer =
      flatAnswers[ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO]?.answer

    const tasks: ServicePlanTaskInfo[] | undefined = taskInfoQuestionnaireAnswer
      ?.map((taskInfoAnswer) => {
        const taskInfoItem = taskInfoAnswer?.item ?? []

        const id = getAnswerValuesFromItems(
          taskInfoItem,
          ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_ID
        )?.at(0)

        const adl = getAnswerValuesFromItems(
          taskInfoItem,
          ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_ADL
        )?.at(0)

        const serviceType = getAnswerValuesFromItems(
          taskInfoItem,
          ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_SERVICE_TYPE
        )?.at(0)

        const directions = getAnswerValuesFromItems(
          taskInfoItem,
          ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_DIRECTIONS
        )?.at(0)

        const frequency =
          getAnswerValuesFromItems(
            taskInfoItem,
            ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_FREQUENCY
          ) ?? []

        return {
          id,
          adl,
          serviceType,
          directions,
          frequency
        }
      })
      ?.filter(isFilledServicePlanTask)
    return {
      [ServicePlanFormField.TASKS]: tasks ?? []
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    if (!this.formDataFieldExists(ServicePlanFormField.TASKS)) {
      return []
    }

    const tasks = this.getFormDataField(ServicePlanFormField.TASKS) as
      | ServicePlanTaskInfo[]
      | undefined

    const taskAnswers =
      tasks?.filter(isFilledServicePlanTask).map((task) => ({
        item: createAnswerItemsFromValues([
          [ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_ID, task.id],
          [ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_ADL, task.adl],
          [
            ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_SERVICE_TYPE,
            task.serviceType
          ],
          [
            ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_DIRECTIONS,
            task.directions
          ],
          [
            ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO_FREQUENCY,
            task.frequency
          ]
        ])
      })) ?? null

    return [
      {
        linkId: ServicePlanQuestionnaireItem.SERVICE_PLAN_PATIENT_TASKS_TASK_INFO,
        value: taskAnswers
      }
    ]
  }
}
